import { useState } from "react";
import { Phone, Mail, Globe, MapPin, Smartphone, ArrowUpRight, ChevronRight, FileText, Download } from "lucide-react";
import { fileUrl } from "@/lib/api";
import { telHref, waHref, mapsHref, BLOCK_LABELS } from "@/lib/cardUtils";
import { LinkIcon, SOCIAL_ICONS, FaWhatsapp } from "@/components/card/icons";
import { MONO } from "@/components/card/theme";

const ext = (href) => (href.startsWith("http") ? { target: "_blank", rel: "noopener noreferrer" } : {});

export const SectionTitle = ({ t, index, children }) => {
  if (!children) return null;
  if (t.tpl === "future")
    return (
      <div className="mb-4 flex items-center gap-3 text-[10px] uppercase tracking-[0.3em]" style={{ color: t.c.accent, fontFamily: MONO }}>
        <span>{String(index + 1).padStart(2, "0")}</span>
        <span className="h-px w-6" style={{ background: t.c.accent }} />
        <span style={{ color: t.c.text }}>{children}</span>
      </div>
    );
  if (t.tpl === "creative")
    return (
      <div className="mb-4 flex items-baseline gap-3" style={{ color: t.c.accent }}>
        <span className="text-xs tabular-nums" style={{ fontFamily: t.body.fontFamily }}>{String(index + 1).padStart(2, "0")}</span>
        <span className="text-2xl italic" style={t.heading}>{children}</span>
      </div>
    );
  return (
    <div className="mb-3 text-[11px] font-semibold uppercase tracking-[0.16em]" style={{ color: t.tpl === "corporate" ? t.c.accent : t.c.text, fontFamily: t.body.fontFamily }}>
      {children}
    </div>
  );
};

const Glyph = ({ img, Icon, size = 18 }) =>
  img ? <img src={fileUrl(img)} alt="" className="object-contain" style={{ width: size + 6, height: size + 6 }} /> : <Icon style={{ width: size, height: size }} strokeWidth={1.6} />;

const QuickAction = ({ t, href, label, Icon, img, onClick, testId }) => (
  <a href={href} {...ext(href)} onClick={onClick} data-testid={testId} className="group flex flex-col items-center gap-1.5 transition-transform duration-150 active:scale-95">
    <span className="grid h-12 w-12 place-items-center overflow-hidden transition-opacity group-hover:opacity-80" style={t.quick}>
      <Glyph img={img} Icon={Icon} />
    </span>
    <span className="text-[11px]" style={{ color: t.c.text }}>{label}</span>
  </a>
);

const Row = ({ t, href, Icon, label, value, onClick, testId }) => {
  const inner = (
    <>
      <span className="grid h-9 w-9 shrink-0 place-items-center" style={t.chip}>
        <Icon className="h-4 w-4" strokeWidth={1.6} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[11px] uppercase tracking-wider opacity-70">{label}</span>
        <span className="block truncate text-[15px]" style={{ color: t.c.title }}>{value}</span>
      </span>
      {href && <ArrowUpRight className="h-4 w-4 opacity-40" />}
    </>
  );
  const cls = "flex items-center gap-3 py-2.5";
  return href ? <a href={href} {...ext(href)} onClick={onClick} className={cls} data-testid={testId}>{inner}</a> : <div className={cls}>{inner}</div>;
};

export const ContactsSection = ({ t, contacts: c, track }) => {
  const call = c.mobile || c.phone;
  const quick = [
    call && { href: telHref(call), label: "Chiama", Icon: Phone, k: "Telefono", ik: "call" },
    c.whatsapp && { href: waHref(c.whatsapp, c.whatsapp_message), label: "WhatsApp", Icon: FaWhatsapp, k: "WhatsApp", ik: "whatsapp" },
    c.email && { href: `mailto:${c.email}`, label: "Email", Icon: Mail, k: "Email", ik: "email" },
    c.website && { href: c.website, label: "Sito", Icon: Globe, k: "Sito web", ik: "website" },
    c.address && c.show_maps && { href: mapsHref(c), label: "Mappa", Icon: MapPin, k: "Google Maps", ik: "maps" },
  ].filter(Boolean);
  const rows = [
    c.mobile && { href: telHref(c.mobile), Icon: Smartphone, label: "Cellulare", value: c.mobile },
    c.phone && { href: telHref(c.phone), Icon: Phone, label: "Telefono", value: c.phone },
    c.email && { href: `mailto:${c.email}`, Icon: Mail, label: "Email", value: c.email },
    c.website && { href: c.website, Icon: Globe, label: "Sito web", value: c.website.replace(/^https?:\/\/(www\.)?/, "").replace(/\/$/, "") },
    c.address && { href: c.show_maps ? mapsHref(c) : "", Icon: MapPin, label: "Indirizzo", value: c.address },
    ...(c.extra || []).map((e) => ({ href: e.url, Icon: ArrowUpRight, label: e.label, value: e.value })),
  ].filter(Boolean);
  if (!quick.length && !rows.length) return null;
  return (
    <div>
      {quick.length > 0 && (
        <div className="mb-5 flex justify-center gap-5" data-testid="public-quick-actions">
          {quick.map((q) => <QuickAction key={q.label} t={t} {...q} img={t.icons?.[q.ik]} testId={`public-action-${q.label.toLowerCase()}`} onClick={() => track("link_click", q.k)} />)}
        </div>
      )}
      <div className="divide-y" style={{ borderColor: t.c.border }}>
        {rows.map((r, i) => <div key={i} style={{ borderColor: t.c.border }}><Row t={t} {...r} onClick={() => track("link_click", r.label)} /></div>)}
      </div>
    </div>
  );
};

export const SocialsSection = ({ t, socials, track }) => {
  const items = Object.entries(socials || {}).filter(([, v]) => v);
  if (!items.length) return null;
  return (
    <div className={`flex flex-wrap gap-3 ${t.tpl === "creative" ? "" : "justify-center"}`} data-testid="public-socials">
      {items.map(([k, url]) => {
        const I = SOCIAL_ICONS[k];
        return (
          <a key={k} href={url} {...ext(url)} aria-label={k} data-testid={`public-social-${k}`} onClick={() => track("link_click", k)}
            className="grid h-11 w-11 place-items-center overflow-hidden transition-transform duration-150 hover:-translate-y-0.5"
            style={t.social}>
            {t.icons?.[k] ? <img src={fileUrl(t.icons[k])} alt="" className="h-6 w-6 object-contain" /> : I && <I className="h-4 w-4" />}
          </a>
        );
      })}
    </div>
  );
};

export const LinksSection = ({ t, links, track }) => {
  if (!links?.length) return null;
  return (
    <div className="space-y-2.5">
      {links.map((l, i) => (
        <a key={l.id} href={l.url || "#"} {...ext(l.url || "")} onClick={() => track("link_click", l.title)} data-testid={`public-link-${i}`}
          className="flex items-center gap-3 px-4 py-3.5 transition-transform duration-150 hover:-translate-y-0.5 active:scale-[0.99]"
          style={l.is_cta ? t.primary : t.secondary}>
          {l.icon_url ? <img src={fileUrl(l.icon_url)} alt="" className="h-6 w-6 shrink-0 object-contain" /> : t.tpl === "creative" && !l.is_cta ? <span className="w-6 text-xs tabular-nums opacity-60">{String(i + 1).padStart(2, "0")}</span> : <LinkIcon name={l.icon} className="h-[18px] w-[18px] shrink-0" />}
          <span className={`min-w-0 flex-1 ${t.tpl === "minimal" && !l.description ? "text-center" : ""}`}>
            <span className="block text-[15px] font-medium leading-tight">{l.title}</span>
            {l.description && <span className="mt-0.5 block text-xs opacity-70">{l.description}</span>}
          </span>
          {t.tpl !== "minimal" && <ChevronRight className="h-4 w-4 shrink-0 opacity-50" />}
        </a>
      ))}
    </div>
  );
};

const TextBlock = ({ data }) => <p className="whitespace-pre-line text-[15px] leading-relaxed">{data.body}</p>;

const WhatsAppBlock = ({ t, data, track, fallback }) => {
  const [msg, setMsg] = useState("");
  const number = data.number || fallback;
  if (!number) return null;
  const send = () => {
    track("link_click", "WhatsApp messaggio");
    window.open(waHref(number, msg.trim()), "_blank", "noopener");
  };
  return (
    <div className="p-3" style={{ ...t.surface, boxShadow: "none" }} data-testid="public-whatsapp-block">
      <textarea value={msg} onChange={(e) => setMsg(e.target.value)} rows={3} data-testid="public-whatsapp-input"
        placeholder={data.placeholder || "Scrivi il tuo messaggio…"} className="w-full resize-none bg-transparent px-1 text-[15px] outline-none placeholder:opacity-50" style={{ color: t.c.title }} />
      <button onClick={send} data-testid="public-whatsapp-send" className="mt-2 flex w-full items-center justify-center gap-2 px-4 py-3 text-[15px] font-semibold transition-opacity hover:opacity-90"
        style={{ background: "#25D366", color: "#fff", borderRadius: t.btnR }}>
        <FaWhatsapp className="h-[18px] w-[18px]" /> {data.button_label || "Invia su WhatsApp"}
      </button>
    </div>
  );
};

const ImageBlock = ({ t, data, track }) => {
  if (!data.url) return null;
  const img = <img src={fileUrl(data.url)} alt={data.alt || ""} loading="lazy" className="w-full object-cover" style={{ borderRadius: t.r }} />;
  return (
    <figure>
      {data.link ? <a href={data.link} {...ext(data.link)} onClick={() => track("link_click", data.alt || "Immagine")}>{img}</a> : img}
      {data.caption && <figcaption className="mt-2 text-xs opacity-70">{data.caption}</figcaption>}
    </figure>
  );
};

const HoursBlock = ({ t, data }) => (
  <div>
    <dl className="divide-y" style={{ borderColor: t.c.border }}>
      {(data.rows || []).map((r, i) => (
        <div key={i} className="flex justify-between gap-4 py-2 text-[15px]" style={{ borderColor: t.c.border }}>
          <dt>{r.day}</dt><dd style={{ color: t.c.title }}>{r.hours}</dd>
        </div>
      ))}
    </dl>
    {data.note && <p className="mt-2 text-xs opacity-70">{data.note}</p>}
  </div>
);

const DividerBlock = ({ t, data }) => {
  if (data.style === "space") return <div className="h-6" />;
  if (data.style === "dots") return <div className="text-center tracking-[0.6em]" style={{ color: t.c.accent }}>···</div>;
  return <hr style={{ borderColor: t.c.border }} />;
};

const ButtonBlock = ({ t, data, track }) =>
  data.label ? (
    <a href={data.url || "#"} {...ext(data.url || "")} onClick={() => track("link_click", data.label)} data-testid="public-extra-button"
      className="block px-4 py-3.5 text-center text-[15px] font-medium transition-opacity hover:opacity-90"
      style={data.variant === "outline" ? { ...t.secondary, background: "transparent", border: `1px solid ${t.c.button}`, color: t.c.button } : t.primary}>
      {data.label}
    </a>
  ) : null;

const ServicesBlock = ({ t, data }) => (
  <ul className="space-y-3">
    {(data.items || []).filter((i) => i.name).map((s, i) => (
      <li key={i} className="flex items-start justify-between gap-4">
        <div><div className="text-[15px] font-medium" style={{ color: t.c.title }}>{s.name}</div>{s.description && <div className="text-xs opacity-75">{s.description}</div>}</div>
        {s.price && <div className="shrink-0 text-sm" style={{ color: t.c.accent }}>{s.price}</div>}
      </li>
    ))}
  </ul>
);

const FileBlock = ({ t, data, track }) =>
  data.url ? (
    <a href={fileUrl(data.url)} target="_blank" rel="noopener noreferrer" onClick={() => track("link_click", data.label || data.filename || "PDF")} data-testid="public-file-block"
      className="flex items-center gap-3 px-4 py-3.5 transition-transform duration-150 hover:-translate-y-0.5" style={t.secondary}>
      <span className="grid h-10 w-10 shrink-0 place-items-center" style={t.chip}><FileText className="h-5 w-5" strokeWidth={1.6} /></span>
      <span className="min-w-0 flex-1">
        <span className="block text-[15px] font-medium leading-tight">{data.label || data.filename || "Documento PDF"}</span>
        {data.description && <span className="mt-0.5 block text-xs opacity-70">{data.description}</span>}
      </span>
      <Download className="h-4 w-4 shrink-0 opacity-60" />
    </a>
  ) : null;

const RENDERERS = { text: TextBlock, image: ImageBlock, hours: HoursBlock, divider: DividerBlock, button: ButtonBlock, services: ServicesBlock, file: FileBlock };
const UNTITLED = ["divider", "button", "image", "file"];

export const Block = ({ t, block, card, track, index }) => {
  const { type, data = {} } = block;
  const c = card.contacts || {};
  if (type === "contacts" && !(c.mobile || c.phone || c.whatsapp || c.email || c.website || c.address || (c.extra || []).length)) return null;
  if (type === "socials" && !Object.values(card.socials || {}).some(Boolean)) return null;
  if (type === "links" && !(card.links || []).length) return null;
  if (type === "whatsapp" && !(data.number || c.whatsapp)) return null;
  let content = null;
  if (type === "contacts") content = <ContactsSection t={t} contacts={card.contacts} track={track} />;
  else if (type === "socials") content = <SocialsSection t={t} socials={card.socials} track={track} />;
  else if (type === "links") content = <LinksSection t={t} links={card.links} track={track} />;
  else if (type === "whatsapp") content = <WhatsAppBlock t={t} data={data} track={track} fallback={c.whatsapp} />;
  else {
    const R = RENDERERS[type];
    content = R ? <R t={t} data={data} track={track} /> : null;
  }
  if (!content) return null;
  const title = UNTITLED.includes(type) ? (type === "file" ? data.title : "") : data.title || (type === "links" || type === "socials" ? "" : BLOCK_LABELS[type]);
  const boxed = t.boxed && type !== "divider";
  return (
    <section data-testid={`public-block-${type}`} className={boxed ? "p-5" : ""} style={boxed ? t.surface : undefined}>
      <SectionTitle t={t} index={index}>{title}</SectionTitle>
      {content}
    </section>
  );
};
